import { motion } from 'framer-motion';
import { ArrowUpRight, Check } from 'lucide-react';
import { TREATMENTS, COMPARISON_ROWS, SITE } from '@/content/site';
import { TID } from '@/constants/testIds';

const ease = [0.16, 1, 0.3, 1];

export default function Treatments() {
  return (
    <section id="treatments" className="py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16 lg:mb-24">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease }}
          >
            <div className="text-overline mb-7">The Two Treatments</div>
            <h2 className="font-display text-[1.75rem] sm:text-3xl lg:text-[2.5rem] leading-[1.15] max-w-2xl">
              Two premium lash lift systems.
              <span className="italic text-[hsl(var(--primary))] font-medium"> One studio.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, ease, delay: 0.1 }}
            className="max-w-md text-[hsl(var(--muted-foreground))] leading-relaxed"
          >
            Most lash lift studios in Vancouver offer one brand. We offer two — so we can
            match the right treatment to your lashes, lifestyle and budget.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {TREATMENTS.map((t, i) => (
            <motion.div
              key={t.id}
              data-testid={TID.treatmentCard(t.id)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, ease, delay: i * 0.1 }}
              className="luxe-hover-lift hover:luxe-shadow relative rounded-[1.5rem] bg-[hsl(var(--cream-2))]/70 border border-[hsl(var(--hairline))] p-9 lg:p-12 flex flex-col"
            >
              <div className="flex items-center gap-3 mb-7">
                <span className="text-xl" aria-hidden>{t.flag}</span>
                <span className="text-[0.65rem] tracking-[0.32em] uppercase text-[hsl(var(--primary))] font-light">
                  {t.badge}
                </span>
              </div>

              <h3 className="font-display text-[1.7rem] lg:text-[2rem] tracking-tight leading-[1.15]">
                {t.name}
              </h3>

              <p className="mt-6 text-[hsl(var(--muted-foreground))] leading-[1.8]">
                {t.blurb}
              </p>

              <ul className="mt-8 space-y-3.5">
                {t.bullets.map((b) => (
                  <li key={b.k} className="flex items-start gap-3 text-[0.92rem]">
                    <Check className="w-3.5 h-3.5 mt-1 text-[hsl(var(--primary))] shrink-0" strokeWidth={1.5} />
                    <span>
                      <span className="font-medium tracking-tight">{b.k}:</span>{' '}
                      <span className="text-[hsl(var(--muted-foreground))]">{b.v}</span>
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-12 flex items-end justify-between pt-7 border-t border-[hsl(var(--hairline))]">
                <div>
                  <div className="text-[0.62rem] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] font-light">
                    Starting at
                  </div>
                  <div className="font-display text-4xl lg:text-[2.75rem] tracking-tight mt-1.5 tabular">
                    {t.price}
                  </div>
                </div>
                <a
                  data-testid={TID.treatmentBookBtn(t.id)}
                  href={SITE.booking}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[0.72rem] tracking-[0.24em] uppercase font-light text-[hsl(var(--primary))] hover:gap-3 transition-all duration-500"
                >
                  Book <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.25} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.95, ease }}
          className="mt-20 lg:mt-24 rounded-[1.5rem] border border-[hsl(var(--hairline))] overflow-hidden"
        >
          <div className="grid grid-cols-3 bg-[hsl(var(--warm-ink))] text-[hsl(var(--cream))]">
            <div className="px-6 lg:px-9 py-6 text-[0.62rem] tracking-[0.32em] uppercase text-[hsl(var(--champagne))]/70 font-light">
              Side by Side
            </div>
            <div className="px-6 lg:px-9 py-6 font-display text-lg lg:text-xl tracking-tight">YUMI Keratin</div>
            <div className="px-6 lg:px-9 py-6 font-display text-lg lg:text-xl tracking-tight">InLei Lash Filler®</div>
          </div>
          {COMPARISON_ROWS.map((row, idx) => (
            <div
              key={row.feature}
              className={`grid grid-cols-3 text-[0.92rem] lg:text-base ${
                idx % 2 === 0 ? 'bg-[hsl(var(--background))]' : 'bg-[hsl(var(--cream-2))]/60'
              }`}
            >
              <div className="px-6 lg:px-9 py-5 font-medium text-[hsl(var(--foreground))]/85 tracking-tight">
                {row.feature}
              </div>
              <div className="px-6 lg:px-9 py-5 text-[hsl(var(--muted-foreground))]">{row.yumi}</div>
              <div className="px-6 lg:px-9 py-5 text-[hsl(var(--muted-foreground))]">{row.inlei}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
